import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface CurriculumAssignment {
  id: string;
  curriculum_id: string;
  user_id: string;
  assigned_by: string;
  assigned_at: string;
  acknowledged_at: string | null;
  curricula?: {
    title: string;
  };
  profiles?: {
    full_name: string;
  };
}

export interface CourseAssignment {
  id: string;
  course_id: string;
  user_id: string;
  assigned_by: string;
  assigned_at: string;
  acknowledged_at: string | null;
  courses?: {
    title: string;
  };
  profiles?: {
    full_name: string;
  };
}

export function useUnacknowledgedAssignments() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["unacknowledged_assignments", user?.id],
    queryFn: async () => {
      if (!user?.id) return { curricula: [], courses: [] };

      const [curriculaRes, coursesRes] = await Promise.all([
        supabase
          .from("curriculum_assignments")
          .select("*, curricula(title)")
          .eq("user_id", user.id)
          .is("acknowledged_at", null),
        supabase
          .from("course_assignments")
          .select("*, courses(title)")
          .eq("user_id", user.id)
          .is("acknowledged_at", null),
      ]);

      return {
        curricula: (curriculaRes.data || []) as CurriculumAssignment[],
        courses: (coursesRes.data || []) as CourseAssignment[],
      };
    },
    enabled: !!user?.id,
  });
}

export function useAcknowledgeAssignments() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      curriculumIds,
      courseIds,
    }: {
      curriculumIds: string[];
      courseIds: string[];
    }) => {
      const now = new Date().toISOString();

      const promises = [];

      if (curriculumIds.length > 0) {
        promises.push(
          supabase
            .from("curriculum_assignments")
            .update({ acknowledged_at: now })
            .in("id", curriculumIds)
            .eq("user_id", user?.id)
        );
      }

      if (courseIds.length > 0) {
        promises.push(
          supabase
            .from("course_assignments")
            .update({ acknowledged_at: now })
            .in("id", courseIds)
            .eq("user_id", user?.id)
        );
      }

      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unacknowledged_assignments"] });
    },
  });
}

export function useAssignCurriculum() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      curriculumId,
      userId,
    }: {
      curriculumId: string;
      userId: string;
    }) => {
      // Check if already assigned
      const { data: existing } = await supabase
        .from("curriculum_assignments")
        .select("id")
        .eq("curriculum_id", curriculumId)
        .eq("user_id", userId)
        .maybeSingle();

      if (existing) {
        throw new Error("User is already assigned to this curriculum");
      }

      const { error } = await supabase.from("curriculum_assignments").insert({
        curriculum_id: curriculumId,
        user_id: userId,
        assigned_by: user?.id,
      });

      if (error) throw error;

      // Also enroll the user in the curriculum if not already enrolled
      const { data: enrollment } = await supabase
        .from("curriculum_enrollments")
        .select("id")
        .eq("curriculum_id", curriculumId)
        .eq("user_id", userId)
        .maybeSingle();

      if (!enrollment) {
        await supabase.from("curriculum_enrollments").insert({
          curriculum_id: curriculumId,
          user_id: userId,
        });

        // Also enroll in all published courses within the curriculum
        const { data: curriculumCourses } = await supabase
          .from("curriculum_courses")
          .select("course_id, courses(is_published)")
          .eq("curriculum_id", curriculumId);

        if (curriculumCourses) {
          const publishedCourseIds = curriculumCourses
            .filter((cc: any) => cc.courses?.is_published)
            .map((cc: any) => cc.course_id);

          if (publishedCourseIds.length > 0) {
            await supabase.from("course_enrollments").insert(
              publishedCourseIds.map((courseId: string) => ({
                course_id: courseId,
                user_id: userId,
              }))
            );
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["curriculum_assignments"] });
    },
  });
}

export function useAssignCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      courseId,
      userId,
    }: {
      courseId: string;
      userId: string;
    }) => {
      // Check if already assigned
      const { data: existing } = await supabase
        .from("course_assignments")
        .select("id")
        .eq("course_id", courseId)
        .eq("user_id", userId)
        .maybeSingle();

      if (existing) {
        throw new Error("User is already assigned to this course");
      }

      const { error } = await supabase.from("course_assignments").insert({
        course_id: courseId,
        user_id: userId,
        assigned_by: user?.id,
      });

      if (error) throw error;

      // Also enroll the user in the course if not already enrolled
      const { data: enrollment } = await supabase
        .from("course_enrollments")
        .select("id")
        .eq("course_id", courseId)
        .eq("user_id", userId)
        .maybeSingle();

      if (!enrollment) {
        await supabase.from("course_enrollments").insert({
          course_id: courseId,
          user_id: userId,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course_assignments"] });
    },
  });
}

export function useCurriculumAssignments(curriculumId: string) {
  return useQuery({
    queryKey: ["curriculum_assignments", curriculumId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curriculum_assignments")
        .select("*")
        .eq("curriculum_id", curriculumId);

      if (error) throw error;

      // Fetch profiles separately
      const userIds = data?.map((d) => d.user_id) || [];
      if (userIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

      return data.map((assignment) => ({
        ...assignment,
        profiles: profileMap.get(assignment.user_id) || { full_name: "Unknown" },
      })) as CurriculumAssignment[];
    },
    enabled: !!curriculumId,
  });
}

export function useCourseAssignments(courseId: string) {
  return useQuery({
    queryKey: ["course_assignments", courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_assignments")
        .select("*")
        .eq("course_id", courseId);

      if (error) throw error;

      // Fetch profiles separately
      const userIds = data?.map((d) => d.user_id) || [];
      if (userIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

      return data.map((assignment) => ({
        ...assignment,
        profiles: profileMap.get(assignment.user_id) || { full_name: "Unknown" },
      })) as CourseAssignment[];
    },
    enabled: !!courseId,
  });
}
